import React from 'react';
import styled from 'styled-components';

type TagType = 'success' | 'danger' | 'warning' | 'info' | 'default';

interface TagProps {
  type?: TagType;
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'filled' | 'outlined' | 'soft';
  icon?: React.ReactNode;
  onRemove?: () => void;
}

const getColorScheme = (type: TagType, variant: 'filled' | 'outlined' | 'soft') => {
  const schemes = {
    success: {
      filled: {
        background: '#4CAF50',
        color: '#fff',
        border: '#4CAF50',
      },
      outlined: {
        background: 'transparent',
        color: '#4CAF50',
        border: '#4CAF50',
      },
      soft: {
        background: '#E8F5E9',
        color: '#2E7D32',
        border: '#E8F5E9',
      },
    },
    danger: {
      filled: {
        background: '#F44336',
        color: '#fff',
        border: '#F44336',
      },
      outlined: {
        background: 'transparent',
        color: '#F44336',
        border: '#F44336',
      },
      soft: {
        background: '#FFEBEE',
        color: '#C62828',
        border: '#FFEBEE',
      },
    },
    warning: {
      filled: {
        background: '#FF9800',
        color: '#fff',
        border: '#FF9800',
      },
      outlined: {
        background: 'transparent',
        color: '#FF9800',
        border: '#FF9800',
      },
      soft: {
        background: '#FFF3E0',
        color: '#E65100',
        border: '#FFF3E0',
      },
    },
    info: {
      filled: {
        background: '#2196F3',
        color: '#fff',
        border: '#2196F3',
      },
      outlined: {
        background: 'transparent',
        color: '#2196F3',
        border: '#2196F3',
      },
      soft: {
        background: '#E3F2FD',
        color: '#1565C0',
        border: '#E3F2FD',
      },
    },
    default: {
      filled: {
        background: '#9E9E9E',
        color: '#fff',
        border: '#9E9E9E',
      },
      outlined: {
        background: 'transparent',
        color: '#616161',
        border: '#9E9E9E',
      },
      soft: {
        background: '#F5F5F5',
        color: '#424242',
        border: '#F5F5F5',
      },
    },
  };

  return schemes[type][variant];
};

const getSizeStyles = (size: 'small' | 'medium' | 'large') => {
  const sizes = {
    small: {
      padding: '4px 8px',
      fontSize: '11px',
      lineHeight: '16px',
      borderRadius: '4px',
    },
    medium: {
      padding: '6px 12px',
      fontSize: '13px',
      lineHeight: '18px',
      borderRadius: '6px',
    },
    large: {
      padding: '8px 16px',
      fontSize: '14px',
      lineHeight: '20px',
      borderRadius: '8px',
    },
  };

  return sizes[size];
};

const StyledTag = styled.span<{
  $type: TagType;
  $size: 'small' | 'medium' | 'large';
  $variant: 'filled' | 'outlined' | 'soft';
  $hasRemove: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.2s ease;
  border-width: 1px;
  border-style: solid;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;

  ${({ $type, $variant }) => {
    const colors = getColorScheme($type, $variant);
    return `
      background-color: ${colors.background};
      color: ${colors.color};
      border-color: ${colors.border};
    `;
  }}

  ${({ $size }) => {
    const sizes = getSizeStyles($size);
    return `
      padding: ${sizes.padding};
      font-size: ${sizes.fontSize};
      line-height: ${sizes.lineHeight};
      border-radius: ${sizes.borderRadius};
    `;
  }}

  ${({ $hasRemove }) =>
    $hasRemove &&
    `
    padding-right: 4px;
  `}

  &:hover {
    opacity: 0.9;
  }
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;

const RemoveButton = styled.button<{
  $type: TagType;
  $variant: 'filled' | 'outlined' | 'soft';
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  line-height: 1;

  ${({ $type, $variant }) => {
    const colors = getColorScheme($type, $variant);
    return `
      color: ${colors.color};
    `;
  }}

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }

  &:active {
    background-color: rgba(0, 0, 0, 0.2);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const Tag: React.FC<TagProps> = ({
  type = 'default',
  children,
  style,
  className,
  size = 'medium',
  variant = 'soft',
  icon,
  onRemove,
}) => {
  return (
    <StyledTag
      $type={type}
      $size={size}
      $variant={variant}
      $hasRemove={!!onRemove}
      style={style}
      className={className}
    >
      {icon && <IconWrapper>{icon}</IconWrapper>}
      {children}
      {onRemove && (
        <RemoveButton
          $type={type}
          $variant={variant}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label="Remove tag"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </RemoveButton>
      )}
    </StyledTag>
  );
};

export default Tag;